import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EFFORTS, type Effort, useModelCatalogue } from "@/lib/llmModels";
import ModelPicker from "../ModelPicker";

const EFFORT_LABELS: Record<Effort, string> = { low: "Low", medium: "Medium", high: "High" };

// Settings only — nothing runs from here. Opened from the model badge in the
// action bar, and governs fill, regenerate and apply alike.
const ModelSheet = ({
  open,
  onOpenChange,
  model,
  onModelChange,
  effort,
  onEffortChange,
  notes,
  onNotesChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  model: string;
  onModelChange: (id: string) => void;
  effort: Effort;
  onEffortChange: (v: Effort) => void;
  notes: string;
  onNotesChange: (v: string) => void;
}) => {
  const { models } = useModelCatalogue();
  const selected = models.find((m) => m.id === model);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Generation settings</DrawerTitle>
          <DrawerDescription>Used for filling a card, regenerating it, and applying queued changes.</DrawerDescription>
        </DrawerHeader>
        <div className="space-y-4 px-4 pb-2">
          <div className="space-y-1.5">
            <Label>Model</Label>
            <ModelPicker value={model} onChange={onModelChange} />
            {selected && (
              <p className="text-[11px] text-muted-foreground">
                {selected.label} · ${selected.inPerM}/${selected.outPerM} per M tokens
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Thinking effort</Label>
            <div className="flex gap-1.5">
              {EFFORTS.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => onEffortChange(value)}
                  className={`flex-1 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                    effort === value
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input bg-background text-muted-foreground"
                  }`}
                >
                  {EFFORT_LABELS[value]}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea
              placeholder="Anything the model should know? e.g. Pérez has a grid penalty"
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DrawerFooter>
          <Button className="w-full font-semibold" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default ModelSheet;
